import React, { useCallback, useContext, useEffect, useState } from 'react';

import { Table, TableBody, TableContainer } from '@mui/material';

import NoAttendanceView from './NoAttendanceView';
import SummaryTableHead from './SummaryTableHead';
import SummaryTableRow from './SummaryTableRow';
import { Order, SummaryTableColumns } from './types';
import { useAttendanceData } from './useAttendanceData';
import { AttendanceContext, View } from '../../context/AttendanceContext';
import { getComparator } from '../../utils/sortingUtils';

const SummaryTable: React.FC = () => {
    const { view } = useContext(AttendanceContext);
    const { sessionSummaryRows, studentSummaryRows } = useAttendanceData();

    const [order, setOrder] = useState<Order>('asc');
    const [orderBy, setOrderBy] = useState<SummaryTableColumns>(() =>
        view === View.StudentSummary ? 'sortName' : 'date'
    );

    useEffect(() => {
        switch (view) {
            case View.SessionSummary:
                setOrderBy('sortName');
                break;
            case View.StudentSummary:
                setOrderBy('date');
                break;
            default:
                break;
        }
    }, [view]);

    const handleRequestSort = useCallback(
        (event: React.MouseEvent<unknown>, property: SummaryTableColumns) => {
            const sortProperty = property === 'name' ? 'sortName' : property;

            const isAsc = orderBy === sortProperty && order === 'asc';

            setOrder(isAsc ? 'desc' : 'asc');
            setOrderBy(sortProperty);
        },
        [order, orderBy]
    );

    const renderTableBody = useCallback(() => {
        let sorted;

        if (view === View.StudentSummary) {
            sorted = studentSummaryRows.slice().sort(getComparator(order, orderBy));
        }

        if (view === View.SessionSummary) {
            sorted = sessionSummaryRows.slice().sort(getComparator(order, orderBy));
        }

        return sorted.map((row, index) => <SummaryTableRow key={index} row={row} />);
    }, [order, orderBy, studentSummaryRows, sessionSummaryRows, view]);

    if (studentSummaryRows.length < 1 && sessionSummaryRows.length < 1) {
        return <NoAttendanceView />;
    }

    return (
        <TableContainer>
            <Table aria-labelledby="student-attendance-table" size="medium">
                <SummaryTableHead order={order} orderBy={orderBy} onRequestSort={handleRequestSort} />
                <TableBody>{renderTableBody()}</TableBody>
            </Table>
        </TableContainer>
    );
};

export default SummaryTable;
